<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/crm';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import type { Person } from '$lib/types';

  let { verticalId, companyId, people = [], value = $bindable({}), idPrefix = 'company-contact' }: {
    verticalId?: number; companyId?: number; people?: Person[];
    value?: Record<string, unknown>; idPrefix?: string;
  } = $props();
  const client = useQueryClient();
  let mode = $state('none');
  let search = $state('');
  let contactId = $state<number | undefined>();
  let firstName = $state('');
  let lastName = $state('');
  let phone = $state('');
  let email = $state('');
  let error = $state('');
  let busy = $state(false);
  let scopeRevision = 0;
  $effect(() => {
    verticalId;
    companyId;
    scopeRevision++;
    error = '';
    mode = 'none'; contactId = undefined; search = ''; firstName = ''; lastName = ''; phone = ''; email = '';
  });
  $effect(() => {
    value = mode === 'existing' ? { contact_id: contactId } : mode === 'new'
      ? { contact: { first_name: firstName, last_name: lastName, phone, email } } : {};
  });
  const query = createQuery(() => ({
    queryKey: ['company-contact-search', verticalId, search],
    queryFn: () => peopleApi.list({ vertical_id: verticalId, q: search || undefined, per_page: 25 }),
    enabled: mode === 'existing' && verticalId !== undefined
  }));
  async function link() {
    if (busy || !companyId || mode === 'none') return;
    if (mode === 'existing' && !contactId || mode === 'new' && !firstName.trim() && !lastName.trim()) {
      error = 'Choose a contact or enter a contact name.'; return;
    }
    busy = true; error = '';
    const requestCompanyId = companyId;
    const requestVerticalId = verticalId;
    const revision = scopeRevision;
    const isCurrent = () => companyId === requestCompanyId && verticalId === requestVerticalId && scopeRevision === revision;
    try {
      await apiFetch(`/companies/${requestCompanyId}/contacts`, { method: 'POST', body: JSON.stringify(value) });
      await client.invalidateQueries({ queryKey: ['company-detail', requestCompanyId] });
      await client.invalidateQueries({ queryKey: ['people'] });
      if (isCurrent()) mode = 'none';
    } catch (err) { if (isCurrent()) error = getErrorMessage(err); } finally { busy = false; }
  }
</script>

<div class="space-y-3">
  {#if companyId}
    <h3 class="label">Linked contacts</h3>
    {#each people as person (person.id)}
      <div class="text-sm">
        <a class="font-semibold text-toyama-teal underline" href={`#/people/${person.id}`}>{`${person.first_name} ${person.last_name}`.trim()}</a>
        {#if person.phone}<div>{person.phone}</div>{/if}
        {#if person.email}<div>{person.email}</div>{/if}
      </div>
    {:else}<p class="text-sm text-dark-gray">No contacts linked yet.</p>{/each}
  {/if}
  <label class="label" for={`${idPrefix}-mode`}>{companyId ? 'Add contact' : 'Primary contact (optional)'}</label>
  <select id={`${idPrefix}-mode`} class="input" bind:value={mode}>
    <option value="none">No contact</option><option value="existing">Link existing contact</option><option value="new">Create new contact</option>
  </select>
  {#if mode === 'existing'}
    <label class="label" for={`${idPrefix}-search`}>Search same-team contacts</label>
    <input id={`${idPrefix}-search`} class="input" bind:value={search} />
    <label class="label" for={`${idPrefix}-existing`}>Existing contact</label>
    <select id={`${idPrefix}-existing`} class="input" bind:value={contactId} required>
      <option value={undefined}>Choose a contact</option>
      {#each query.data?.results ?? [] as person (person.id)}
        <option value={person.id}>{person.first_name} {person.last_name} — {person.phone || person.email}</option>
      {/each}
    </select>
    {#if query.isError}<p class="text-sm text-red-600">{getErrorMessage(query.error)}</p>{/if}
  {:else if mode === 'new'}
    <label class="label" for={`${idPrefix}-first`}>Contact first name</label><input id={`${idPrefix}-first`} class="input" bind:value={firstName} required />
    <label class="label" for={`${idPrefix}-last`}>Contact last name</label><input id={`${idPrefix}-last`} class="input" bind:value={lastName} />
    <label class="label" for={`${idPrefix}-phone`}>Contact phone</label><input id={`${idPrefix}-phone`} class="input" bind:value={phone} />
    <label class="label" for={`${idPrefix}-email`}>Contact email</label><input id={`${idPrefix}-email`} type="email" class="input" bind:value={email} />
    <p class="text-xs text-dark-gray">Saved on the person only, together with the company link. Sales ownership follows the team's existing policy.</p>
  {/if}
  {#if companyId && mode !== 'none'}<button type="button" class="btn-secondary" disabled={busy} onclick={link}>{busy ? 'Saving…' : 'Save contact link'}</button>{/if}
  {#if error}<p class="text-sm text-red-600">{error}</p>{/if}
</div>
